class ProductPopup extends HTMLElement {
  constructor() {
    super();
    this.popupSection = this.closest('.section-product-popup');
    this.closeButton = this.querySelector('.product-close-popup');
    this.form = this.querySelector('#product-block-form');
    this.contentType = this.getAttribute('data-popup-trigger');
    this.maxShowTimes = parseInt(this.getAttribute('data-show-product'), 10) || 1;

    this.POPUP_DELAY = 6000;
    this.POPUP_DELAY_SCROLL = 10;

    this.closeCount = Number(localStorage.getItem('productPopupCloseCount')) || 0;
    this.formSubmitted = localStorage.getItem('productFormSubmitted') === 'true';
    this.popupShown = false;

    this.previouslyFocusedElement = null;
    this.lastInteractionWasKeyboard = false;

    this.init();
  }

  init() {
    if (!this.popupSection || this.closeCount >= this.maxShowTimes || this.formSubmitted) return;

    this.setupEventListeners();
    this.handleInitialScroll();
    setTimeout(() => this.handlePageLoadTimeout(), this.POPUP_DELAY);
  }

  setupEventListeners() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        this.lastInteractionWasKeyboard = true;
      }
    });

    const markPointerInteraction = () => {
      this.lastInteractionWasKeyboard = false;
    };

    document.addEventListener('mousedown', markPointerInteraction);
    document.addEventListener('touchstart', markPointerInteraction, { passive: true });

    this.closeButton?.addEventListener('click', () => this.hidePopup(false));
    this.closeButton?.addEventListener('keydown', (event) => {
      const isEnter = event.key === 'Enter';
      const isSpace = event.key === ' ' || event.key === 'Spacebar';
      const isEscape = event.key === 'Escape' || event.key === 'Esc';
      if (isEnter || isSpace || isEscape) {
        event.preventDefault();
        this.hidePopup(true);
      }
    });
  
    document.addEventListener('keydown', (event) => {
      const isEscape = event.key === 'Escape' || event.key === 'Esc';
      if (!isEscape) return;
      if (this.popupSection?.classList.contains('popup-visible')) {
        this.hidePopup(true);
      }
    });
  
    this.form?.addEventListener('submit', () => {
      localStorage.setItem('productFormSubmitted', 'true');
    });
  
    window.addEventListener('scroll', this.checkScroll.bind(this));
  }

  checkScroll() {
    if (this.getScrolledPercentage() > this.POPUP_DELAY_SCROLL) {
      window.removeEventListener('scroll', this.checkScroll);
      this.showPopup();
    }
  }

  handlePageLoadTimeout() {
    this.showPopup();
  }

  handleInitialScroll() {
    if (this.getScrolledPercentage() > this.POPUP_DELAY_SCROLL) {
      this.showPopup();
    }
  }

  getScrolledPercentage() {
    return ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100;
  }

  async showPopup() {
    if (this.popupShown) return;
  
    this.previouslyFocusedElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
  
    if (this.contentType === 'recently_product') {
      const raw = localStorage.getItem('recently-viewed-popup');
      if (!raw) return;
  
      let handle;
      try {
        const productData = JSON.parse(raw);
        handle = productData.productHandle;
      } catch (e) {
        console.error('Invalid JSON in recently-viewed-popup');
        return;
      }
  
      if (!handle) return;
  
      try {
        const fetchedData = await this.fetchProductData(handle);
        if (!fetchedData) return;
  
        this.updatePopupContent(fetchedData);
      } catch (e) {
        console.error('Failed to fetch product data:', e);
        return;
      }
    }
  
    this.popupSection.classList.remove('popup-hidden');
    this.popupSection.classList.add('popup-visible');
    this.popupShown = true;
  
    this.popupSection.setAttribute('tabindex', '-1');
  
    const initialFocus =
      this.closeButton ||
      this.querySelector('.product-button a') ||
      this.popupSection;
  
    setTimeout(() => {
      if (this.lastInteractionWasKeyboard) {
        if (typeof trapFocus === 'function') {
          trapFocus(this.popupSection, initialFocus);
        }
        if (initialFocus && typeof initialFocus.focus === 'function') {
          initialFocus.focus();
        }
      }
    }, 50);
  }


  hidePopup(fromKeyboard = false) {
    if (!this.popupSection) return;
  
    this.popupSection.classList.remove('popup-visible');
    this.popupSection.classList.add('popup-hidden');
    localStorage.setItem('productPopupCloseCount', ++this.closeCount);
  
    const elementToFocus =
      fromKeyboard &&
      this.previouslyFocusedElement &&
      document.contains(this.previouslyFocusedElement)
        ? this.previouslyFocusedElement
        : null;
  
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(elementToFocus || null);
    } else if (elementToFocus && typeof elementToFocus.focus === 'function') {
      setTimeout(() => {
        if (document.contains(elementToFocus)) {
          elementToFocus.focus();
        }
      }, 100);
    }
  
    this.previouslyFocusedElement = null;
  }

  addShopifyImageWidth(url, width = 600) {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  async fetchProductData(handle) {
    const url = `/products/${handle}.js`;

    const response = await fetch(url);
    if (!response.ok) return null;

    const product = await response.json();
    const variant = product.variants.find(v => v.available) || product.variants[0];

    const formatMoney = (cents) => {
      const currency = window.cartStrings?.currency || "$";
      return `${currency}${(Number(cents) / 100).toFixed(2)}`;
    };

    return {
      productImage: product.media?.[0]?.preview_image?.src || 
                   product.media?.[0]?.src || 
                   product.featured_image || 
                   product.images?.[0] || '',
      productTitle: product.title,
      productUrl: `/products/${product.handle}`,
      productPrice: formatMoney(variant.price),
      productPrice2: variant.compare_at_price ? formatMoney(variant.compare_at_price) : ''
    };
  }

  updatePopupContent(productData) {
    if (!productData) return;

    const mediaBox = this.querySelector('.media-box img');
    if (mediaBox) {
      const optimizedImage = this.addShopifyImageWidth(productData.productImage, 690);
      mediaBox.setAttribute('src', optimizedImage);
      mediaBox.setAttribute('alt', productData.productTitle);
    }

    const title = this.querySelector('.product-block-title');
    if (title) title.textContent = productData.productTitle;

    const priceSale = this.querySelector('.price-item--sale');
    if (priceSale) priceSale.innerHTML = productData.productPrice;

    const priceRegular = this.querySelector('.price-item--regular');
    if (priceRegular) priceRegular.innerHTML = productData.productPrice2;

    const button = this.querySelector('.product-button a');
    if (button) button.setAttribute('href', productData.productUrl);
  }
}

customElements.define('product-popup', ProductPopup);